"use client";

import { startTransition, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import useSWR from "swr";
import { AnalyticsEventOnMount } from "@/shared/analytics/trackAnalyticsEvent";
import { useHotelStore } from "@/shared/stores/hotel-store/HotelStoreProvider";
import type { HotelDetailBySlugResponse } from "@/types/services/hotel/apis/Detail";
import { mapHotelBookingToV2 } from "../adapters/hotelBookingV2.adapter";
import type {
  HotelDetailV2Model,
} from "../model/hotelDetailV2.model";
import AboutSection from "./AboutSection";
import AmenitiesSection from "./AmenitiesSection";
import BookingPanel, { SelectedRoomsCard } from "./BookingPanel";
import DetailAnchorTabs from "./DetailAnchorTabs";
import DetailBreadcrumb from "./DetailBreadcrumb";
import DetailTopbar from "./DetailTopbar";
import FaqSection from "./FaqSection";
import HotelGallery from "./HotelGallery";
import HotelIdentity from "./HotelIdentity";
import LocationSection from "./LocationSection";
import MobileBookingBar from "./MobileBookingBar";
import ReviewsSection from "./ReviewsSection";
import RoomsSection, { RoomsSkeleton } from "./RoomsSection";
import RulesSection from "./RulesSection";

type BookingSearch = Pick<SearchParams, "checkIn" | "checkOut" | "rooms">;
type EffectiveSearch = {
  routeKey: string;
  search: BookingSearch;
};

interface HotelDetailV2ShellProps {
  hotel: HotelDetailV2Model;
  defaultSearch: BookingSearch;
}

export default function HotelDetailV2Shell({
  hotel,
  defaultSearch,
}: HotelDetailV2ShellProps) {
  const routeSearchParams = useSearchParams();
  const detailNavigationContext = useHotelStore(
    (state) => state.detailNavigationContext,
  );
  const setDetailNavigationContext = useHotelStore(
    (state) => state.setDetailNavigationContext,
  );
  const setDetailRooms = useHotelStore((state) => state.setDetailRooms);
  const [effectiveSearch, setEffectiveSearch] =
    useState<EffectiveSearch | null>(null);
  const routeSearch = routeSearchParams.toString();
  const explicitSearch = useMemo(
    () => getExplicitSearch(new URLSearchParams(routeSearch), defaultSearch.rooms),
    [defaultSearch.rooms, routeSearch],
  );
  const routeKey = `${hotel.city.iata}/${hotel.slug}?${routeSearch}`;

  useEffect(() => {
    setDetailRooms([]);
  }, [routeKey, setDetailRooms]);

  useEffect(() => {
    if (explicitSearch) {
      startTransition(() =>
        setEffectiveSearch({ routeKey, search: explicitSearch }),
      );
      if (detailNavigationContext) setDetailNavigationContext(null);
      return;
    }

    const hasMatchingNavigationContext =
      detailNavigationContext?.city.toUpperCase() ===
        hotel.city.iata.toUpperCase() &&
      detailNavigationContext.slug === hotel.slug;

    if (hasMatchingNavigationContext) {
      setDetailRooms([]);
      startTransition(() =>
        setEffectiveSearch({
          routeKey,
          search: {
            checkIn: detailNavigationContext.checkIn,
            checkOut: detailNavigationContext.checkOut,
            rooms: detailNavigationContext.rooms,
          },
        }),
      );
      setDetailNavigationContext(null);
      return;
    }

    if (detailNavigationContext) setDetailNavigationContext(null);

    startTransition(() =>
      setEffectiveSearch((current) =>
        current?.routeKey === routeKey
          ? current
          : { routeKey, search: defaultSearch },
      ),
    );
  }, [
    defaultSearch,
    detailNavigationContext,
    explicitSearch,
    hotel.city.iata,
    hotel.slug,
    routeKey,
    setDetailRooms,
    setDetailNavigationContext,
  ]);

  const activeSearch =
    effectiveSearch?.routeKey === routeKey ? effectiveSearch.search : null;
  const availabilityUrl = activeSearch
    ? createAvailabilityUrl(hotel.slug, activeSearch)
    : null;
  const {
    data: availabilityResponse,
    error: availabilityError,
    isLoading: isAvailabilityLoading,
  } = useSWR<HotelDetailBySlugResponse | undefined>(
    availabilityUrl,
    fetchHotelBookingAvailability,
    { revalidateOnFocus: false },
  );
  const booking = useMemo(
    () => {
      const bookingSearch = activeSearch ?? defaultSearch;
      const mappedBooking = mapHotelBookingToV2(
        availabilityResponse,
        bookingSearch,
      );

      return availabilityError
        ? {
            ...mappedBooking,
            availabilityMessage:
              "دریافت اطلاعات اتاق‌ها با خطا مواجه شد. لطفاً دوباره تلاش کنید.",
          }
        : mappedBooking;
    },
    [activeSearch, availabilityError, availabilityResponse, defaultSearch],
  );
  const preserveUrlSearch = Boolean(explicitSearch);
  const isLoading = !activeSearch || isAvailabilityLoading;
  const isSearchResolved = Boolean(activeSearch);
  const isAvailabilityReady =
    isSearchResolved && !isAvailabilityLoading && !availabilityError;

  const handleStayChange = (nextSearch: BookingSearch) => {
    setDetailRooms([]);
    setEffectiveSearch({ routeKey, search: nextSearch });
  };

  return (
    <main
      className="min-h-[70vh] overflow-x-clip bg-[#f5f6f8] pb-28 lg:pb-12"
      dir="rtl"
    >
      {isSearchResolved ? (
        <>
          <AnalyticsEventOnMount
            eventName="hotel_detail_viewed"
            funnel="hotel_booking"
            step="hotel_detail"
            properties={{
              city: hotel.city.iata,
              hotelSlug: hotel.slug,
              hotelName: hotel.name,
              checkIn: booking.checkIn,
              checkOut: booking.checkOut,
              nights: booking.nights,
              rooms: booking.roomCount,
              adults: booking.adults,
              children: booking.children,
            }}
          />
          <DetailTopbar
            booking={booking}
            preserveUrlSearch={preserveUrlSearch}
            onStayChange={handleStayChange}
            hotelName={hotel.name}
          />
        </>
      ) : null}

      <div className="customContainer mx-auto max-md:!px-4 md:px-4">
        <DetailBreadcrumb hotel={hotel} />
        <HotelIdentity hotel={hotel} />
        <HotelGallery images={hotel.images} hotelName={hotel.name} />
        <div className="sticky top-12 z-30 -mx-4 mt-4 h-11 border-b border-[#e5e7eb] bg-white md:hidden">
          <DetailAnchorTabs className="h-full" />
        </div>

        <div className="mt-6 flex flex-col items-start gap-5 lg:flex-row">
          <div className="min-h-[680px] w-full min-w-0 flex-1">
            <div className="space-y-5">
              <AmenitiesSection
                facilities={hotel.facilities}
                hotelName={hotel.name}
              />
              {isLoading ? (
                <RoomsSkeleton />
              ) : (
                <RoomsSection
                  booking={booking}
                  city={hotel.city.iata}
                  hotelImageUrl={hotel.images[0]?.url}
                  slug={hotel.slug}
                />
              )}
              <AboutSection hotel={hotel} />
              {/* TODO: Restore hotel facts after the final detail data contract is ready. */}
              {/* <HotelFacts booking={booking} /> */}
              <LocationSection hotel={hotel} />
              <ReviewsSection hotel={hotel} />
              {/* TODO: Restore similar hotels after it is connected to real data. */}
              {/* <SimilarHotelsSection hotel={hotel} /> */}
              {/* TODO: Restore comparison after it is connected to real data. */}
              {/* <ComparisonSection hotel={hotel} /> */}
              <RulesSection hotel={hotel} />
              {hotel.faqs.length > 0 ? <FaqSection hotel={hotel} /> : null}
              {/* TODO: Restore nearby hotels links after they are connected to real data. */}
              {/* <NearbyLinksSection hotel={hotel} /> */}
            </div>
          </div>
          <aside id="booking-panel" className="hidden w-full scroll-mt-28 lg:sticky lg:top-16 lg:z-20 lg:block lg:w-[260px] lg:shrink-0 lg:self-start">
            <div className="space-y-3">
              {isAvailabilityReady ? (
                <SelectedRoomsCard
                  booking={booking}
                  city={hotel.city.iata}
                  slug={hotel.slug}
                />
              ) : null}
              {isSearchResolved ? (
                <BookingPanel
                  booking={booking}
                  preserveUrlSearch={preserveUrlSearch}
                  onStayChange={handleStayChange}
                />
              ) : null}
            </div>
          </aside>
        </div>
      </div>
      {isSearchResolved ? (
        <MobileBookingBar
          booking={booking}
          city={hotel.city.iata}
          slug={hotel.slug}
        />
      ) : null}
    </main>
  );
}

function getExplicitSearch(
  searchParams: URLSearchParams,
  defaultRooms: string,
): BookingSearch | null {
  const checkIn = searchParams.get("checkIn")?.trim();
  const checkOut = searchParams.get("checkOut")?.trim();
  const rooms = searchParams.get("rooms")?.trim();

  if (!checkIn || !checkOut) return null;

  return { checkIn, checkOut, rooms: rooms || defaultRooms };
}

function createAvailabilityUrl(slug: string, search: BookingSearch) {
  const params = new URLSearchParams({
    slug,
    checkIn: search.checkIn,
    checkOut: search.checkOut,
    rooms: search.rooms,
  });

  return `/api/hotel/detail-v2/?${params.toString()}`;
}

async function fetchHotelBookingAvailability(url: string) {
  const response = await fetch(url, { cache: "no-store" });

  if (!response.ok) {
    throw new Error(`Hotel availability request failed with ${response.status}`);
  }

  return response.json() as Promise<HotelDetailBySlugResponse>;
}
